import _                                    from 'underscore';
import { useCallback, useEffect, useState } from 'react';

import { UI_DISPLAY_MODES }   from '../constants/UIEnums';
import { toggleUserSnapTabs } from '../views/components/util/external/UserSnapForm';
import { usePrefStates }      from './usePrefStates';

export type UIDisplayModeValue = boolean | null;
export type UIDisplayModesHashType = Record<UI_DISPLAY_MODES, UIDisplayModeValue>

interface UseUIDisplayModesHook {
	
	currentModes: UI_DISPLAY_MODES[] | null,
	
	/* Turn on/off a UI mode */
	toggleMode: ( mode: UI_DISPLAY_MODES, isActive?: UIDisplayModeValue ) => void,
	
	/* Turn on/off multiple UI modes */
	toggleModes: ( modes: UIDisplayModesHashType ) => void,
	
	/* Inspect active state of mode */
	isModeActive: ( mode: UI_DISPLAY_MODES ) => boolean,
	
	/* Is user viewing the platform with the smallest possible chrome footprint */
	minimizedChrome: boolean,
	
	/* Is user viewing the platform with the smallest possible activity map footprint */
	minimizedActivityMap: boolean,
	
	/* Is user viewing the platform with the smallest possible roadmap footprint */
	minimizedRoadmap: boolean,
	
	/* Is user viewing the platform with the smallest possible budget by category grid footprint */
	minimizedBudgetByCategory: boolean,
	
	/* Is user viewing the platform with the smallest possible budget by hire grid footprint */
	minimizedBudgetByHire: boolean,
}

const useUIDisplayModes = (): UseUIDisplayModesHook => {
	
	let [ prefState, prefs, prefEnums ]   = usePrefStates(),
	    prefKey                           = prefEnums.UI_DISPLAY_MODES,
	    [ currentModes, setCurrentModes ] = useState<UI_DISPLAY_MODES[]>( prefs.get( prefKey ) );
	
	const handleStorageUpdate = useCallback( ( e ) => {
		
		// hide / show anything external
		toggleUserSnapTabs( isModeActive( UI_DISPLAY_MODES.CHROME_MINIMIZED ) );
		
		// set current modes
		setCurrentModes( prefs.get( prefKey ) );
		
	}, [ currentModes ] );
	
	// when pref states change, throw a dispatch
	useEffect( () => {
		
		window.dispatchEvent( new Event( 'storage' ) );
		
	}, [ prefState ] )
	
	useEffect( () => {
		
		window.addEventListener( 'storage', handleStorageUpdate );
		
		return () => {
			
			window.removeEventListener( 'storage', handleStorageUpdate );
		}
		
	}, [] );
	
	const toggleMode = useCallback<UseUIDisplayModesHook['toggleMode']>( ( mode, isActive = null ) => {
		
		let currentModes       = prefs.get( prefKey ),
		    newModeSet,
		    shouldToggleActive = _.isNull( isActive ) ? !( isModeActive( mode ) ) : isActive;
		
		newModeSet = ( shouldToggleActive ) ? [ ...currentModes, mode ] : _.without( currentModes, mode );
		
		prefs.update( prefKey, _.uniq( newModeSet ) );
		
	}, [ prefState ] );
	
	const toggleModes = useCallback<UseUIDisplayModesHook['toggleModes']>( ( modes ) => {
		
		let currentModes = prefs.get( prefKey ),
		    newModeSet   = currentModes;
		
		_.mapObject( modes, ( isActive: UIDisplayModeValue, mode: UI_DISPLAY_MODES ) => {
			
			let shouldToggleActive = _.isNull( isActive ) ? !( isModeActive( mode ) ) : isActive;
			
			newModeSet = ( shouldToggleActive ) ? [ ...newModeSet, mode ] : _.without( newModeSet, mode );
			
		} );
		
		prefs.update( prefKey, _.uniq( newModeSet ) );
		
	}, [ prefState ] );
	
	const isModeActive = useCallback<UseUIDisplayModesHook['isModeActive']>( ( mode ) => {
		
		let currentModes = prefs.get( prefKey );
		
		return currentModes.includes( mode );
		
	}, [ prefState ] );
	
	return {
		minimizedChrome:           isModeActive( UI_DISPLAY_MODES.CHROME_MINIMIZED ),
		minimizedActivityMap:      isModeActive( UI_DISPLAY_MODES.ACTIVITY_MAP_MINIMIZED ),
		minimizedRoadmap:          isModeActive( UI_DISPLAY_MODES.ROADMAP_MINIMIZED ),
		minimizedBudgetByCategory: isModeActive( UI_DISPLAY_MODES.BUDGET_BY_CATEGORY_MINIMIZED ),
		minimizedBudgetByHire:     isModeActive( UI_DISPLAY_MODES.BUDGET_BY_HIRE_MINIMIZED ),
		currentModes,
		toggleMode,
		toggleModes,
		isModeActive,
	};
}

export { useUIDisplayModes }
